import React, { useState } from "react";
import { productRepo } from "../../services";
import { ensureInventoryRows, nextRowCode } from "../../services/commerce";
import { useApp } from "../../context/AppContext";
import { useI18n } from "../../i18n";
import { Badge, Button, DataTable, Input, Modal, PageHead, Select, Switch, baht, useAsync, useTableState } from "../../components/ui";

const TYPES = [
  { value: "service", label: "บริการ" },
  { value: "product", label: "สินค้า" },
  { value: "package", label: "แพ็กเกจ" },
  { value: "rental", label: "รายการเช่า" },
];
const EMPTY = { name: "", type: "service", price: 0, cost: 0, unit: "ครั้ง" };

export default function Catalog() {
  const { orgId, branches, toast } = useApp();
  const { tx } = useI18n();
  const tableState = useTableState();
  const [type, setType] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);

  const { data, loading, reload } = useAsync(
    () =>
      productRepo.list(orgId, {
        page: tableState.page,
        perPage: tableState.perPage,
        search: tableState.search,
        filter: (row) => !type || row.type === type,
      }),
    [orgId, type, tableState.page, tableState.perPage, tableState.search]
  );

  const toggleActive = async (row) => {
    await productRepo.update(orgId, row.id, { isActive: !row.isActive });
    reload();
  };

  const save = async () => {
    if (!form.name || !form.price) return toast(tx("กรอกชื่อและราคาให้ครบ", "Name and price required"), "error");
    const created = await productRepo.create(orgId, {
      code: await nextRowCode(productRepo, orgId, { prefix: "P", docType: "product" }),
      ...form,
      category: TYPES.find((t) => t.value === form.type)?.label,
      isActive: true,
      trackStock: form.type === "product",
    });
    // สินค้าจับต้องได้ -> สร้างแถวสต็อก (qty 0) ให้ทุกสาขา เพื่อให้ปรับสต็อก/ขายตัดสต็อกได้
    if (created?.id) await ensureInventoryRows(orgId, created, branches);
    setOpen(false);
    setForm(EMPTY);
    reload();
    toast(tx("เพิ่มรายการเรียบร้อย", "Item created"), "success");
  };

  return (
    <>
      <PageHead
        title={tx("สินค้า & บริการ", "Products & services")}
        subtitle={tx("ตั้งค่ารายการสินค้า บริการ คอร์ส หรือรายการเช่า", "Manage products, services, courses or rentals")}
        actions={<Button variant="primary" onClick={() => setOpen(true)}>+ {tx("เพิ่มรายการ", "Add item")}</Button>}
      />

      <div className="filter-bar">
        <input className="input-crm" style={{ maxWidth: 260 }} placeholder={tx("ค้นหาชื่อ / รหัส", "Search name / code")} value={tableState.search} onChange={(e) => tableState.setSearch(e.target.value)} />
        <Select options={TYPES} value={type} onChange={(e) => setType(e.target.value)} placeholder={tx("ทุกประเภท", "All types")} style={{ width: "auto" }} />
      </div>

      <DataTable
        loading={loading}
        rows={data?.rows || []}
        total={data?.total || 0}
        page={tableState.page}
        perPage={tableState.perPage}
        onPage={tableState.setPage}
        onPerPage={tableState.setPerPage}
        emptyText={tx("ยังไม่มีรายการ", "No items")}
        columns={[
          { key: "code", label: tx("รหัส", "Code") },
          { key: "name", label: tx("ชื่อรายการ", "Name") },
          { key: "type", label: tx("ประเภท", "Type"), render: (row) => <Badge tone="indigo">{row.category}</Badge> },
          { key: "price", label: tx("ราคาขาย", "Price"), align: "right", render: (row) => baht(row.price) },
          { key: "cost", label: tx("ต้นทุน", "Cost"), align: "right", render: (row) => baht(row.cost) },
          { key: "unit", label: tx("หน่วย", "Unit") },
          { key: "isActive", label: tx("เปิดขาย", "Active"), render: (row) => <Switch checked={row.isActive} onChange={() => toggleActive(row)} /> },
        ]}
      />

      <Modal
        open={open}
        title={tx("เพิ่มรายการใหม่", "New item")}
        onClose={() => setOpen(false)}
        footer={
          <>
            <Button onClick={() => setOpen(false)}>{tx("ยกเลิก", "Cancel")}</Button>
            <Button variant="primary" onClick={save}>{tx("บันทึก", "Save")}</Button>
          </>
        }
      >
        <Input label={tx("ชื่อรายการ", "Name")} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        <Select label={tx("ประเภท", "Type")} options={TYPES} value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} />
        <div className="grid-2">
          <Input label={tx("ราคาขาย", "Price")} type="number" value={form.price} onChange={(e) => setForm({ ...form, price: Number(e.target.value) })} />
          <Input label={tx("ต้นทุน", "Cost")} type="number" value={form.cost} onChange={(e) => setForm({ ...form, cost: Number(e.target.value) })} />
        </div>
        <Input label={tx("หน่วยนับ", "Unit")} value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} />
      </Modal>
    </>
  );
}
